import { useRef, useState, type MouseEvent } from "react";
import { useContent } from "../../../../contexts/contentContext/contentContext";
import useOnClickOutside from "../../../../hooks/useOnClickOutside";
import Icon, { IconWithTooltip } from "../../../Elements/Icon/Icon";
import CustomPopup from "../Popups/CustomPopup";
import { usePicker } from "../../../../contexts/pickerContext/pickerContext";
import { useSearchParams } from "react-router-dom";

import styles from "./styles.module.scss";
import "./customPopupStylesOverriding.scss";
import { useToasts } from "react-toast-notifications";
import LoadingImageElement from "../../../Elements/LoadingContainerElement/LoadingImageElement";
import { useModal } from "../../../../contexts/modalContext/modalContext";
import Button from "../../../Elements/Button/Button";
import Image from "../../../Elements/Image/Image";

interface PickerActionsI {
  editLink: string;
  shareLink: string;
  handleOpenIframe: (
    e: MouseEvent<HTMLAnchorElement>,
    type: "view" | "edit"
  ) => void;
  pickerGalleryID: string;
}

const actionList = [
  {
    action: "editLink",
    name: "newInbox/Picker/pen-outlined",
    text: "tooltip-edit-picker-project",
  },
  {
    action: "shareLink",
    name: "newInbox/Picker/share",
    text: "tooltip-view-picker-project",
  },
  {
    action: "copyLink",
    name: "newinbox/link",
    text: "tooltip-copy-link-picker-project",
  },
  {
    action: "archive",
    name: "newinbox/picker-archive",
    text: "tooltip-archive-picker-project",
  },
  {
    action: "unarchive",
    name: "newinbox/picker-archive",
    text: "tooltip-unarchive-picker-project",
  },
  {
    action: "remove",
    name: "newinbox/picker-remove",
    text: "tooltip-remove-picker-project",
  },
];

const PickerActions = ({
  handleOpenIframe,
  pickerGalleryID,
  ...props
}: PickerActionsI) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const { getTextByKey, getHtmlByKey } = useContent();
  const ref = useRef(null);
  const { archiveProject, unarchiveProject, removeProject, removeProjectById } =
    usePicker();
  const [searchParams] = useSearchParams();
  const { addToast } = useToasts();
  const { setModal, closeModal } = useModal();
  const [isActionPerforming, setIsActionPerforming] = useState(false);

  useOnClickOutside(ref, () => {
    setShowDropdown(false);
  });

  const handleLinkClick = (
    e: MouseEvent<HTMLAnchorElement>,
    action: string
  ) => {
    e.preventDefault();
    e.stopPropagation();

    handleOpenIframe(e, action === "editLink" ? "edit" : "view");

    setShowDropdown(false);
  };

  const handleClick = async (
    e: MouseEvent<HTMLSpanElement>,
    action: string
  ) => {
    e.stopPropagation();
    setShowDropdown(false);
    switch (action) {
      case "archive":
        setModal({
          modal: () => (
            <div className={styles.confirmDialog}>
              <h2>{getHtmlByKey("archive-project-confirm-title")}</h2>
              <p>{getHtmlByKey("archive-project-confirm-description")}</p>
              <div>
                <Button
                  gray
                  onClick={() => {
                    setIsActionPerforming(false);
                    closeModal();
                  }}
                >
                  {getTextByKey("cancel")}
                </Button>
                <Button
                  disabled={isActionPerforming}
                  onClick={async () => {
                    setIsActionPerforming(true);
                    closeModal();
                    const archiveRes = await archiveProject(pickerGalleryID);
                    if (archiveRes) {
                      addToast(getTextByKey("toast-archive-project"), {
                        appearance: "success",
                      });
                      removeProjectById(pickerGalleryID);
                    } else {
                      addToast(getTextByKey("toast-archive-project-error"), {
                        appearance: "error",
                      });
                    }
                    setIsActionPerforming(false);
                  }}
                >
                  {isActionPerforming ? (
                    <Image name="preloader" ext="gif" alt="" title="" />
                  ) : null}
                  {getTextByKey("confirm-archive")}
                </Button>
              </div>
            </div>
          ),
          modalSize: "full-modal",
        });

        break;
      case "unarchive":
        setIsActionPerforming(true);
        const unarchiveRes = await unarchiveProject(pickerGalleryID);
        if (unarchiveRes) {
          addToast(getTextByKey("toast-unarchive-project"), {
            appearance: "success",
          });
        } else {
          addToast(getTextByKey("toast-unarchive-project-error"), {
            appearance: "error",
          });
        }
        setIsActionPerforming(false);

        break;
      case "remove":
        setModal({
          modal: () => (
            <div className={styles.confirmDialog}>
              <h2>{getHtmlByKey("remove-project-confirm-title")}</h2>
              <p>{getHtmlByKey("remove-project-confirm-description")}</p>
              <div>
                <Button
                  gray
                  onClick={() => {
                    setIsActionPerforming(false);
                    closeModal();
                  }}
                >
                  {getTextByKey("cancel")}
                </Button>
                <Button
                  disabled={isActionPerforming}
                  onClick={async () => {
                    setIsActionPerforming(true);
                    closeModal();
                    const removeRes = await removeProject(pickerGalleryID);
                    if (removeRes) {
                      addToast(getTextByKey("toast-remove-project"), {
                        appearance: "success",
                      });
                      removeProjectById(pickerGalleryID);
                    } else {
                      addToast(getTextByKey("toast-remove-project-error"), {
                        appearance: "error",
                      });
                    }
                    setIsActionPerforming(false);
                  }}
                >
                  {isActionPerforming ? (
                    <Image name="preloader" ext="gif" alt="" title="" />
                  ) : null}
                  {getTextByKey("confirm-remove")}
                </Button>
              </div>
            </div>
          ),
          modalSize: "full-modal",
        });

        break;
      case "copyLink":
        setIsActionPerforming(true);
        navigator.clipboard.writeText(
          (props as { [key: string]: string }).shareLink
        );
        setIsActionPerforming(false);
        break;
      default:
        break;
    }
  };

  const toggleShowActionList = () => {
    setShowDropdown((prev) => !prev);
  };

  return (
    <div
      className={[styles.actionsWrapper, "picker-actions-wrapper"].join(" ")}
      onMouseEnter={toggleShowActionList}
      onMouseLeave={toggleShowActionList}
      onClick={(e) => e.stopPropagation()}
    >
      {isActionPerforming ? (
        <LoadingImageElement className={styles.loadingIcon} />
      ) : (
        <Icon name="dots-vertical" large iconClass={styles.dotsIcon} />
      )}
      <CustomPopup showState={showDropdown}>
        <ul className={styles.actions} ref={ref}>
          {actionList
            ?.filter(({ action }) =>
              searchParams.get("p") === "archived"
                ? action === "unarchive"
                : action !== "unarchive"
            )
            .map(({ action, name, text }) => (
              <li key={action}>
                {action === "editLink" || action === "shareLink" ? (
                  <a
                    href={(props as { [key: string]: string })[action]}
                    onClick={(e) => handleLinkClick(e, action)}
                  >
                    <IconWithTooltip
                      iconProps={{ name, large: true, gray: true }}
                      tooltipProps={{
                        tooltipTextKey: text,
                      }}
                    />
                    <span>{getTextByKey(text)}</span>
                  </a>
                ) : (
                  <span
                    onClick={(e) => handleClick(e, action)}
                    className={styles.actionItem}
                  >
                    <IconWithTooltip
                      iconProps={{ name, large: true, gray: true }}
                      tooltipProps={{
                        tooltipTextKey: text,
                      }}
                    />
                    <span>{getTextByKey(text)}</span>
                  </span>
                )}
              </li>
            ))}
        </ul>
      </CustomPopup>
    </div>
  );
};

export default PickerActions;
